import { API_BASE_URL } from './api.client';

/**
 * Whether a session cookie exists — not what it is.
 *
 * The session itself is HttpOnly and cannot be read here. The server sets a readable companion
 * beside it (see SessionCookie.PresenceName on the API) whose value is a constant "1", issued and
 * cleared together with the session. Reading it tells the page whether there is anything to
 * restore, so a browser with no session never asks the server who it is — and never gets a red
 * 401 in the console for asking. Nothing here is stored by the page: the server writes these.
 */
const HRMS = 'hrms_signed_in';
const PLATFORM = 'hrms_platform_signed_in';

function has(name: string): boolean {
  try {
    return document.cookie.split(';').some(c => c.trim().startsWith(`${name}=`));
  } catch { return false; }
}

/**
 * Can this page read the markers AT ALL?
 *
 * The API appends both cookies with no Domain attribute, which makes them host-only: the browser
 * hands them back only to the exact hostname that set them. That is right for the credential — it
 * keeps the session cookie off every neighbouring subdomain — but it decides whether the readable
 * marker beside it is visible to the page.
 *
 * Cookie scope is by HOSTNAME, and deliberately ignores the port. That single fact is why this was
 * invisible in development and broken in production:
 *
 *   local       page http://localhost:3000  ·  API http://localhost:5082        → same host, VISIBLE
 *   production  page https://hrm.electemb.com · API https://hrm-api.electemb.com → other host, NEVER
 *
 * So in production document.cookie here can never contain the marker, however alive the session is.
 * Treating that absence as "signed out" is a false negative, and an expensive one: the boot check
 * then skips /auth/me and lands on the login page after every reload. A mode switch performs a
 * reload, which is why switching Actual → Compliance looked like being logged out. Where the page
 * and the API differ by hostname the marker carries no information, and the only thing that knows
 * whether a session exists is the server — so ask it.
 */
export function canReadSessionPresence(): boolean {
  try {
    return new URL(API_BASE_URL, window.location.href).hostname === window.location.hostname;
  } catch { return false; }
}

/**
 * "Is it worth asking the server who this is?" True whenever a marker is visible, and also whenever
 * no marker could be visible in the first place. Never a promise that a session exists — only
 * /auth/me settles that.
 */
export const mayHaveHrmsSession = (): boolean => !canReadSessionPresence() || has(HRMS);
export const mayHavePlatformSession = (): boolean => !canReadSessionPresence() || has(PLATFORM);
